import { useState } from 'react';
import { diagnosticQuestions } from '../data/content';
import { sendEmail } from '../services/sendEmail';
import './Diagnostico.css';

function getScore(answers) {
  const yes = Object.values(answers).filter((v) => v === true).length;
  return Math.round((yes / diagnosticQuestions.length) * 100);
}

function getMessage(score) {
  if (score >= 70) return 'Sua empresa possui excelente potencial para reduzir retrabalho através de automação de processos.';
  if (score >= 40) return 'Sua empresa possui bom potencial de automação. Alguns processos podem ser digitalizados com ganhos significativos.';
  return 'Sua empresa já tem processos organizados, mas ainda há oportunidades pontuais de melhoria.';
}

function Diagnostico() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResult, setShowResult] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ nome: '', empresa: '', email: '', whatsapp: '' });
  const [status, setStatus] = useState('idle'); // idle | sending | success | error
  const [errorMessage, setErrorMessage] = useState('');

  const current = diagnosticQuestions[step];
  const isLast = step === diagnosticQuestions.length - 1;
  const score = getScore(answers);

  const handleAnswer = (value) => {
    const updated = { ...answers, [current.id]: value };
    setAnswers(updated);

    if (isLast) {
      setShowResult(true);
    } else {
      setStep(step + 1);
    }
  };

  const handleFormChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    setErrorMessage('');

    const respostas = diagnosticQuestions
      .map((q) => `${q.question}: ${answers[q.id] ? 'SIM' : 'NÃO'}`)
      .join('\n');

    const payload = {
      subject: `Diagnóstico de Automação - ${score}% - ${form.empresa}`,
      replyTo: form.email,
      fields: {
        nome: form.nome,
        empresa: form.empresa,
        email: form.email,
        whatsapp: form.whatsapp,
        indice_automacao: `${score}%`,
        mensagem_resultado: getMessage(score),
        respostas,
      },
    };

    try {
      await sendEmail(payload);
      setStatus('success');
    } catch (err) {
      setStatus('error');
      setErrorMessage(err.message || 'Erro ao enviar. Tente novamente.');
    }
  };

  const reset = () => {
    setStep(0);
    setAnswers({});
    setShowResult(false);
    setShowForm(false);
    setForm({ nome: '', empresa: '', email: '', whatsapp: '' });
    setStatus('idle');
    setErrorMessage('');
  };

  return (
    <div className="diagnostico-page">
      <div className="page-hero">
        <h1>Diagnóstico de Automação</h1>
        <p>Responda 10 perguntas e descubra o potencial de automação da sua empresa.</p>
      </div>

      <section className="section">
        <div className="container">
          <div className="diagnostico-box">
            {!showResult ? (
              <>
                <div className="diagnostico-progress">
                  <span className="progress-text">Pergunta {step + 1} de {diagnosticQuestions.length}</span>
                  <div className="progress-bar">
                    <div className="progress-fill" style={{ width: `${((step + 1) / diagnosticQuestions.length) * 100}%` }} />
                  </div>
                </div>

                <h2 className="diagnostico-question">{current.question}</h2>

                <div className="diagnostico-buttons">
                  <button className="btn btn-primary btn-large" onClick={() => handleAnswer(true)}>SIM</button>
                  <button className="btn btn-outline btn-large" onClick={() => handleAnswer(false)}>NÃO</button>
                </div>

                {step > 0 && (
                  <button className="diagnostico-back" onClick={() => setStep(step - 1)}>← Voltar</button>
                )}
              </>
            ) : !showForm ? (
              <div className="diagnostico-result">
                <p className="result-label">Índice de Automação</p>
                <div className="result-score">{score}%</div>
                <p className="result-message">{getMessage(score)}</p>
                <button className="btn btn-primary btn-large" onClick={() => setShowForm(true)}>
                  Receber resultado completo
                </button>
                <button className="btn btn-outline diagnostico-reset" onClick={reset}>Refazer diagnóstico</button>
              </div>
            ) : status === 'success' ? (
              <div className="diagnostico-success">
                <div className="success-icon" aria-hidden="true">✓</div>
                <h2>Diagnóstico enviado!</h2>
                <p>Recebemos seus dados. Em breve entraremos em contato com a análise personalizada.</p>
                <button className="btn btn-outline diagnostico-reset" onClick={reset}>Fazer novo diagnóstico</button>
              </div>
            ) : (
              <div className="diagnostico-form-wrap">
                <div className="result-mini">
                  <span>Seu índice: <strong>{score}%</strong></span>
                </div>
                <h2>Vamos conversar?</h2>
                <p className="form-subtitle">Preencha seus dados para receber uma análise personalizada.</p>

                <form className="diagnostico-form" onSubmit={handleSubmit}>
                  <div className="form-group">
                    <label htmlFor="nome">Nome</label>
                    <input type="text" id="nome" name="nome" value={form.nome} onChange={handleFormChange} required placeholder="Seu nome" disabled={status === 'sending'} />
                  </div>
                  <div className="form-group">
                    <label htmlFor="empresa">Empresa</label>
                    <input type="text" id="empresa" name="empresa" value={form.empresa} onChange={handleFormChange} required placeholder="Nome da empresa" disabled={status === 'sending'} />
                  </div>
                  <div className="form-group">
                    <label htmlFor="email">E-mail</label>
                    <input type="email" id="email" name="email" value={form.email} onChange={handleFormChange} required placeholder="seu@email.com" disabled={status === 'sending'} />
                  </div>
                  <div className="form-group">
                    <label htmlFor="whatsapp">WhatsApp</label>
                    <input type="tel" id="whatsapp" name="whatsapp" value={form.whatsapp} onChange={handleFormChange} required placeholder="(11) 99999-9999" disabled={status === 'sending'} />
                  </div>

                  {status === 'error' && (
                    <p className="form-error" role="alert">{errorMessage}</p>
                  )}

                  <button type="submit" className="btn btn-primary btn-large" disabled={status === 'sending'}>
                    {status === 'sending' ? 'Enviando...' : 'Enviar diagnóstico'}
                  </button>
                </form>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

export default Diagnostico;
